/**
 * Excel Parser Service - Importação de arquivos XLSX do LegalManager
 */
import * as XLSX from 'xlsx'
import { APP_CONFIG } from '@config/constants'
import ClassificationService from './classification'

const ExcelParserService = {
  ehDiaUtil(data) {
    const d = new Date(data)
    const diaSemana = d.getDay()
    const dataStr = d.toISOString().split('T')[0]
    if (diaSemana === 0 || diaSemana === 6) return false
    if (APP_CONFIG.FERIADOS.includes(dataStr)) return false
    return true
  },

  calcularDataVencimento(dataPublicacao, prazoDias) {
    if (!dataPublicacao || !prazoDias) return dataPublicacao || new Date().toISOString().split('T')[0]
    const inicio = new Date(dataPublicacao + 'T12:00:00')
    let diasContados = 0
    const atual = new Date(inicio)
    while (diasContados < prazoDias) {
      atual.setDate(atual.getDate() + 1)
      if (this.ehDiaUtil(atual)) diasContados++
    }
    return atual.toISOString().split('T')[0]
  },

  normalizarNumero(numero) {
    if (!numero) return ''
    return String(numero).replace(/[^0-9]/g, '')
  },

  parsearData(str) {
    if (!str) return new Date().toISOString().split('T')[0]
    const s = String(str).trim()
    const num = parseFloat(s)
    if (!isNaN(num) && num > 1000) {
      const diasDesdeEpoch = num - 2
      const dataExcel = new Date(diasDesdeEpoch * 86400000)
      if (dataExcel.getFullYear() > 1900 && dataExcel.getFullYear() < 2100) {
        return dataExcel.toISOString().split('T')[0]
      }
    }
    const m1 = s.match(/^(\d{1,2})[/\-](\d{1,2})[/\-](\d{4})$/)
    if (m1) return `${m1[3]}-${m1[2].padStart(2, '0')}-${m1[1].padStart(2, '0')}`
    const m2 = s.match(/^(\d{4})[/\-](\d{1,2})[/\-](\d{1,2})$/)
    if (m2) return `${m2[1]}-${m2[2].padStart(2, '0')}-${m2[3].padStart(2, '0')}`
    const dataTentativa = new Date(s)
    if (!isNaN(dataTentativa.getTime())) return dataTentativa.toISOString().split('T')[0]
    return new Date().toISOString().split('T')[0]
  },

  prazoParaTipo(tipo) {
    const t = String(tipo || '').toLowerCase().trim()
    const mapa = {
      prazo: 15, pro: 15, audiência: 1, audiencia: 1,
      agenda: 7, interno: 5, imp: 30, emb: 10,
    }
    return mapa[t] || 15
  },

  extrairNumeroProcesso(origem) {
    if (!origem) return null
    const texto = String(origem)
    const matches = [...texto.matchAll(/\(([0-9][0-9.\-]+[0-9])\)/g)]
    if (matches.length > 0) {
      for (let i = matches.length - 1; i >= 0; i--) {
        const candidate = matches[i][1].replace(/[.\-]/g, '')
        if (candidate.length >= 8) return matches[i][1]
      }
    }
    const fallback = texto.match(/\d{10,20}/)
    if (fallback) return fallback[0]
    return null
  },

  calcularStatus(dataVencimento, dataRealizacao) {
    const hoje = new Date()
    const vencimento = new Date(dataVencimento)
    if (dataRealizacao) return 'concluido'
    if (hoje > vencimento) return 'atrasado'
    return 'pendente'
  },

  parseLegalManagerXlsx(arrayBuffer) {
    const wb = XLSX.read(arrayBuffer, { type: 'array' })
    const ws = wb.Sheets[wb.SheetNames[0]]
    const rows = XLSX.utils.sheet_to_json(ws, { defval: '', header: 1 })

    if (rows.length < 2) throw new Error('Arquivo sem dados suficientes')

    let headerRow = -1
    const colunas = {}

    for (let i = 0; i < Math.min(15, rows.length); i++) {
      const row = rows[i]
      if (!row) continue
      let numHeaders = 0
      row.forEach((cell, index) => {
        if (cell && typeof cell === 'string' && cell.trim().length > 0) {
          const cl = cell.toLowerCase().trim()
          if (
            cl.includes('data') || cl.includes('processo') || cl.includes('descri') ||
            cl.includes('tipo') || cl.includes('respons') || cl.includes('nº') ||
            cl.includes('agenda') || cl.includes('prazo') || cl.includes('realiz') ||
            cl.includes('cadastro') || cl.includes('advog') || cl.includes('número') ||
            cl.includes('título') || cl.includes('assunto') || cl.includes('tarefa')
          ) {
            numHeaders++
          }
        }
      })
      if (numHeaders >= 3) {
        headerRow = i
        row.forEach((cell, index) => {
          if (cell && typeof cell === 'string') {
            const cl = cell.toLowerCase().trim()
            if (cl.includes('data') && (cl.includes('públic') || cl.includes('início') || cl.includes('public') || cl.includes('inicio') || cl.includes('pub'))) colunas.dataPublicacao = index
            else if (cl.includes('realiz') || (cl.includes('data') && cl.includes('real'))) colunas.dataRealizacao = index
            else if (cl.includes('cadastro') || (cl.includes('data') && cl.includes('cad'))) colunas.dataCadastro = index
            else if (cl.includes('origem') || cl.includes('descri') || cl.includes('titulo') || cl.includes('título') || cl.includes('assunto')) colunas.origem = index
            else if (cl.includes('respons') || cl.includes('advog')) colunas.responsavel = index
            else if (cl.includes('tipo') || cl.includes('natureza')) colunas.tipo = index
            else if (cl.includes('cód') || cl.includes('nº') || cl.includes('num') || cl.includes('código') || cl.includes('processo')) colunas.codigo = index
          }
        })
        break
      }
    }

    if (headerRow === -1) headerRow = 0
    if (Object.keys(colunas).length < 3) {
      colunas.dataPublicacao = 2
      colunas.dataRealizacao = 4
      colunas.dataCadastro = 12
      colunas.origem = 8
      colunas.responsavel = 5
      colunas.tipo = 6
      colunas.codigo = 13
    }

    const result = []
    for (let i = headerRow + 1; i < rows.length; i++) {
      const row = rows[i]
      if (!row || row.every((c) => !c || c === '')) continue

      const origemRaw = String(row[colunas.origem] || '').trim()
      const dataRaw = String(row[colunas.dataPublicacao] || '').trim()
      const realizacaoRaw = String(row[colunas.dataRealizacao] || '').trim()
      const cadastroRaw = String(row[colunas.dataCadastro] || '').trim()
      const responsavel = String(row[colunas.responsavel] || '').trim()
      const tipo = String(row[colunas.tipo] || '').trim()

      const numProcessoRaw = this.extrairNumeroProcesso(origemRaw)
      if (!numProcessoRaw) continue
      const numeroProcesso = this.normalizarNumero(numProcessoRaw)
      if (!numeroProcesso || numeroProcesso.replace(/0/g, '') === '') continue

      const dataPublicacao = this.parsearData(dataRaw)
      const dataRealizacao = this.parsearData(realizacaoRaw)
      const dataCadastro = this.parsearData(cadastroRaw)
      const prazoDias = this.prazoParaTipo(tipo)
      const dataVencimento = this.calcularDataVencimento(dataPublicacao, prazoDias)

      let statusRealizacao = 'pendente'
      let concluido = false
      if (dataRealizacao && dataRealizacao !== dataPublicacao) {
        concluido = true
        statusRealizacao = new Date(dataRealizacao) <= new Date(dataVencimento) ? 'no_prazo' : 'atrasado'
      } else if (new Date() > new Date(dataVencimento)) {
        statusRealizacao = 'nao_realizado'
      }

      result.push({
        numeroProcesso,
        descricao: origemRaw || 'Sem descrição',
        dataPublicacao,
        dataVencimento,
        data: dataVencimento,
        dataRealizacao: concluido ? dataRealizacao : null,
        prazoDias,
        advogado: responsavel || 'Não atribuído',
        responsavel: responsavel || 'Não atribuído',
        status: this.calcularStatus(dataVencimento, concluido ? dataRealizacao : null),
        concluido,
        diasUteisAtraso: 0,
        tipoAgenda: tipo || 'Não especificado',
        origemCompleta: origemRaw,
        statusRealizacao,
        dataCadastro,
        tipo: tipo || 'Não especificado',
        area: ClassificationService.classificarArea(numeroProcesso, origemRaw),
        ultimaAlteracao: new Date().toISOString(),
        usuarioAlteracao: 'sistema',
        criadoPor: 'sistema',
        alteracoesHistorico: [],
      })
    }
    return result
  },
}

export default ExcelParserService
